import {
	all,
	A,
	Br,
	Button,
	Checkbox,
	Div,
	Label,
	LI,
	Input,
	RadioInput,
	RangeInput,
	Span,
	UL,
	H2,
	H3,
	VString,
	TextArea,
	not,
	reactive,
	P
} from 'alkali'
import { activeTab } from 'model/StudyCollection'
import {
	selectedStudies,
	studySelections,
	structuredQuery,
	studyFilter,
} from 'model/SearchQuery'
import { screening, getArticleId } from 'model/Screening'
import './PicoTermContainer.scss'
import { srcAbbrv } from 'model/AnalyzeStudy'
import app from 'app/app'
import AnnotatedAbstract from 'components/extract/model/AnnotatedAbstract'
import { studiesSearch, studySearchCache } from 'model/EvidenceSearch'
import { get } from 'tools/request'
import Icon from '../Icon'
import * as deleteIcon from '../../images/delete.svg'
import * as positiveDirectionalityIcon from 'icons/monitor-heart-rate-up.svg'
import * as negativeDirectionalityIcon from 'icons/monitor-heart-rate-down.svg'
import GroupReassignmentDialog from '../studies/GroupReassignmentDialog'
import Dialog from '../generic/DialogAlkali'

const ArrowIcon = (container, expanded: boolean) => {
	return Span(`.twisty${expanded ? '.expand' : '.collapse'}#${container}-twisty`, {
		onclick: (event) => {
			event.stopPropagation();
			containerCollapse(container)
		},
		content: '➤'
	})
}

const containerCollapse = (container) => {
	const containerElement = document.querySelector(`#${container}`)
	let thisTwisty = document.querySelector(`#${container}-twisty`)
	let expanded = thisTwisty.classList.contains('expand')
	expanded ? thisTwisty.classList.replace('expand', 'collapse') : thisTwisty.classList.replace('collapse', 'expand')
	containerElement.classList.toggle('collapse')
}

const contentExpand = (parent) => {
	const parentElement = document.querySelector(`#${parent}`)
	let updatedView = parentElement.getAttribute('dir')
	if (updatedView == 'ver') {
		parentElement.setAttribute('dir', 'hor')
	} else {
		parentElement.setAttribute('dir', 'ver')
	}
}

// Seperated out in case we want to check any other strings in the results in the future
const checkForMatch = (first, second) => {
	const firstTreated = first.toString().toLowerCase().replace(/\s/g, '')
    const secondTreated = second.toString().toLowerCase().replace(/\s/g, '')
	return firstTreated == secondTreated
}

const onlyChildMatch = (list, parent, child) => {
	const checkChild = list.length == 1
	let childMatch = false
	if(checkChild){
		childMatch = checkForMatch(parent, child)
	}
    return childMatch
}

const removePublication = (publication) => {
	console.log('Article removed from trial grouping:', {
		user: app.user,
		study: publication,
	})
	document.getElementById(`${publication.id}`).remove()
	// set removal flag in study's analysisSelections
	const studySettings = studySelections.property(getArticleId(publication))
	// clear conflicting flags
	if (studySettings.property('groupAdd')) {
		studySettings.set('groupAdd', undefined)
	}
	studySettings.set('groupRemove', true)
	let currentTab = activeTab.valueOf()
	let tabSubscription = studiesSearch.subscribe(() => {
		activeTab.put(currentTab)
	})	
	// display updated studyList
	studySearchCache.clear()
	studiesSearch.updated()
	tabSubscription.unsubscribe()
	document.querySelector('.study-details-pane').hide()
}

const relocatePublication = (target, source) => {
	console.log('Article reassigned to new trial group:', {
		user: app.user,
		sourceStudy: source,
		targetStudy: target,
	})
	const sourceId = getArticleId(source)
	const targetId = getArticleId(target.valueOf())
	let studySettings = studySelections.property(sourceId)
	// clear conflicting flags
	if (studySettings.property('groupRemove')) {
		studySettings.set('groupRemove', undefined)
	}
	// set reassignment flag in study's analysisSelections
	studySettings.set('groupAdd', {
		source: sourceId,
		target: targetId,
	})
	let currentTab = activeTab.valueOf()
	// display updated study list
	let tabSubcription = studiesSearch.subscribe(() => {
		activeTab.put(currentTab)
	})
	studySearchCache.clear()
	studiesSearch.updated() 
	tabSubcription.unsubscribe()
	document.querySelector('.study-details-pane').hide()
}

export default class PicoTermsContainer extends Div('.pico-terms-container') {
	ready(properties) {
		let { study, studyList, publicationList, excludedReason, annotModel } = properties
		let articleId = getArticleId(study)
		let vote = screening.to((screening) =>
			screening?.votes.find((vote) => vote.articleId == articleId)
		)

		const createAttributeHeader = (attribute) =>
			Label([
				Checkbox('.pico-match', {
					checked: true,
					onchange: () => {
						excludedReason.put('not matching ' + attribute)
					},
				}),
				attribute,
			])

		const createAttribute = ({ name, matchingScore, as, parent, isPositive, suggestedPositive }) => {
			return LI(
				'.attribute-link.truncate',
				[
					Span({
						title: app.user?.FeatureConfig?.Debug?.Enabled ? name + (matchingScore ? ': estimated relevance matching likelihood of ' + Math.round(matchingScore * 100) + '%' : '') : '',
						content: [name, typeof isPositive == 'boolean' || typeof suggestedPositive == 'boolean' ? Icon('.outcome-directionality-icon', {
							icon: 
								(
									typeof isPositive == 'boolean'
										? isPositive
										: suggestedPositive
								)
									? positiveDirectionalityIcon
									: negativeDirectionalityIcon,
							title: 
								(
									typeof isPositive == 'boolean'
										? isPositive
										: suggestedPositive
								)
									? 'Increasing ' + name + ' is the desired outcome'
									: 'Reducing ' + name + ' is the desired outcome'
						}) : null]
					}),
					// more ? Button(`#${this.id}.more-button`, {
					// 		content: 'More',
					// 		onclick: () => {s
					// 			contentExpand(parent, more)
					// 		},
					// }) : null,
				],
			)
		}
		const picoContainer = async (picoTerm) => {
			let fullTermList
			let groupLength
			let displayList
			let moreContent
			let parentLabel
			switch (picoTerm) {
				case 'publications':
					{
						fullTermList = publicationList
						groupLength = publicationList?.length > 6
						moreContent = groupLength
						displayList = groupLength ? fullTermList.slice(0, 6) : fullTermList
						return Div('#publications.term-dropdown-container', [
							Div('.term-header', {
								content: Div('.display-flex', [
									Div('.display-flex', [
										H2('.term-title', [fullTermList > 1 ? 'Study Publications' : 'Study Publication']),
										ArrowIcon('publications', true)
									]),
								]),
								onclick: () => {
									containerCollapse('publications')
								},
							}),
							Div('#publications-body.term-body', [
									Div('#publications-container.parent-container', {
										/* ---------- Remove commented code if we want the full list */
										// content: [
										// 	Div('.initial-content', [
										// 		UL(
										// 			(publicationList?.length > 0) ? fullTermList.map((termChild) => {
										// 				let pubLabel = (termChild.year || 'NR') + '  ' + termChild.name
										// 				let { source, srcabbrv } = srcAbbrv(termChild)
										// 				return  LI('.pub-child', [
										// 					Label('.truncate', [pubLabel]),
										// 					Span({classes: 'pub-src', title: source, content: srcabbrv})
										// 				])
										// 			}) : 'No recognized groupings for this characteristic'
										// 		)
										// 	]),
										// ],
										attributes: { dir: 'hor' },
										content: [
											Div('.initial-content', [
												Div('.attribute-list.truncate', [
													UL(moreContent ? '.thin-list' : null, 
														displayList.map((termChild) => {
															let pubLabel = (termChild.year || 'NR') + '  ' + termChild.name
															let { source, srcabbrv } = srcAbbrv(termChild)
															return  LI(`#${termChild.id}.pub-child.truncate`, [
																// Commented out as some publications don't have fullTextUrl values but do have links, need to add further support
																// Label('.truncate', [termChild.fullTextUrl ? A('.pub-link', { href: termChild.fullTextUrl, target: '_blank', rel: 'noopener' }, [pubLabel]) : pubLabel]),
																// Only display group editing controls if the study is digitized
																(displayList.length === 1 || termChild.parentStudy != undefined) && termChild.groups != undefined
																	? Icon('.edit-icon', {
																		title: "Remove this publication from this trial grouping",
																		icon: deleteIcon,
																		onclick() {
																			new GroupEditingDialog({
																				removePublication,
																				relocatePublication,
																				termChild,
																				publicationList,
																				pubLabel
																			}).show()
																		}
																	})
																	: null,
																Label('.pub-label.truncate', [pubLabel]),
																Span({classes: 'pub-src', title: source, content: srcabbrv})
															])
														}),
													),
													moreContent ? Button('#more-publications-container.more-button', {
														content: 'More',
														onclick: () => {
															contentExpand('publications-container', displayList)
														},
													}) : null,
												])
											]),
											Div('.more-content', [
												Div('.attribute-list', [
													UL('.thin-list.truncate', fullTermList.map(
														(termChild) => {
															let pubLabel = (termChild.year || 'NR') + '  ' + termChild.name
															let { source, srcabbrv } = srcAbbrv(termChild)
															return LI('.pub-child', [
																// Commented out as some publications don't have fullTextUrl values but do have links, need to add further support
																// Label('.truncate', [termChild.fullTextUrl ? A('.pub-link', { href: termChild.fullTextUrl, target: '_blank', rel: 'noopener' }, [pubLabel]) : pubLabel]),
																displayList.length === 1 || termChild.parentStudy != undefined
																	? Icon('.edit-icon', {
																		title: "Remove this publication from this trial grouping",
																		icon: deleteIcon,
																		onclick() {
																			new GroupEditingDialog({
																				removePublication,
																				relocatePublication,
																				termChild,
																				publicationList,
																				pubLabel
																			}).show()
																		}
																	})
																	: null,
																Label('.pub-label.truncate', [pubLabel]),
																Span('.pub-src', {title: source, content: srcabbrv})
															])
														})
													),
													Button(`#publication.more-button`, {
														content: 'Hide',
														onclick: () => {
															contentExpand('publications-container')
														},
													})
												])
											])
										],
									})
							]),
						])
					}
					break
				case 'design':
					{
						fullTermList = study.designs.valueOf()
						groupLength = study.designs.valueOf().length
						displayList = groupLength ? study.designs?.slice(0, 2) : study.designs
						return Div('#design.term-dropdown-container', [
							Div('.term-header', {
								content: Div('.display-flex', [
									Div('.display-flex', [
										H2('.term-title', [groupLength > 1 ? 'Study Designs' : 'Study Design']),
										Div('.matching-percent', [
											Span({
												display: studyFilter.designs.to(designs => designs?.length > 0),
												content: asPercentage(study.robScore)
											}),
											ArrowIcon('design', true),
										]),
									]),
								]),
								onclick: () => {
									containerCollapse('design')
								},
							}),
							Div('#design-body.term-body', [
								Div('.initial-content', [
									UL(
										(groupLength > 0) ? study.designs.map((termChild) => {
											return LI([Label(termChild)])
										}) : 'Design information not found'
									),
								]),
							])
						])
					}
					break
				case 'outcomes':
					{
						fullTermList = structuredQuery?.outcomes.valueOf()			//Header set
						let outcomes = (study.outcomes || []).concat(
							AnnotatedAbstract.getAnnotations((await annotModel?.annotatedAbstract)?.annotations || [],
								fullTermList, 'outcome'))
						outcomes = _.uniqBy(outcomes, 'name')		// Children set?
						groupLength = outcomes.length > 4
						let showMore = groupLength && outcomes
						displayList = groupLength ? outcomes.slice(0, 4) : outcomes
						return Div(`#outcomes.term-dropdown-container${(fullTermList.length > 0) ? '': '.collapse' }`, [
							Div('.term-header', {
								content: Div('.display-flex', [
									Div('.display-flex', [
										H2('.term-title', [fullTermList.length > 1 ? 'Outcomes' : 'Outcome']),
										Div('.matching-percent', [
											fullTermList.length > 0 ? asPercentage(study.outcomeScore) : null,
											ArrowIcon('outcomes', true)
										])
									]),
								]),
								onclick: () => {
									containerCollapse('outcomes')
								},
							}),
							Div('#outcomes-body.term-body', [
								fullTermList.length > 0 ? fullTermList.map((termChild) => {
									let parentLabel = termChild.label
									let parentLabelTrimmed = termChild?.label.replace(/\s/g, "").substring(0, 20)
									return Div(`#${parentLabelTrimmed}.parent-container`, {
										attributes: { dir: 'hor' },
										content: [
											Div('.initial-content', [
												createAttributeHeader(parentLabel),
												Div('.attribute-list.truncate', [
													UL(showMore ? '.thin-list' : null, [
														displayList?.map((termChild) => {
															return createAttribute(termChild)
														})
													]),
													showMore ? Button(`#more-${parentLabel}.more-button`, {
														content: 'More',
														onclick: () => {
															contentExpand(parentLabelTrimmed)
														},
													}) : null,
												])
											]),
											Div('.more-content', [
												createAttributeHeader(termChild.label),
												Div('.attribute-list.truncate', [
													UL('.thin-list', outcomes.map((termChild) => createAttribute(termChild))),
													Button(`#more-${parentLabelTrimmed}.hide-button`, {
														content: 'Hide',
														onclick: () => {
															contentExpand(parentLabelTrimmed)
														},
													})
												])
											])
										],
									})
								}) : 'No outcome specified'
							]),
						])
					}
					break
				case 'characteristics':
					{
						let parentList = structuredQuery?.characteristics.valueOf()
						let characteristics = (study.characteristics || []).concat(
							AnnotatedAbstract.getAnnotations((await annotModel?.annotatedAbstract)?.annotations || [],
								parentList, 'characteristic'))
						characteristics = _.uniqBy(characteristics, 'name')
						let showMore = characteristics?.length > 2
						moreContent = showMore && characteristics
						displayList = showMore
							? characteristics.slice(0, 2)
							: characteristics
						// add more link to displayLength dependent on showMore
						return Div(`#characteristics.term-dropdown-container${(parentList.length > 0) ? '': '.collapse' }`, [
							Div('.term-header', {
								content: Div('.display-flex', [
									Div('.display-flex', [
										(parentList.length > 1) ? H2('.term-title', ['Conditions']) : H2('.term-title', ['Condition']),
										(parentList.length > 0) ? Div('.matching-percent', [
											asPercentage(study.charScore),
											ArrowIcon('characteristics', true)
										]) : Div('.matching-percent', [
											ArrowIcon('characteristics', false)
										])
									])
								]),
                                onclick: () => {
                                    containerCollapse('characteristics')
                                },
                            }),
							Div('#characteristics-body.term-body', [
								(parentList.length > 0) ? parentList.map((parentTerm) => {
									let parentLabel = parentTerm.label
									let parentLabelTrimmed = parentTerm.label.replace(/\s/g, "").substring(0, 20)
									return Div(`#${parentLabelTrimmed}.parent-container`, {
										attributes: { dir: 'hor' },
										content: [
											Div('.initial-content', [
												createAttributeHeader(parentLabel),
												Div('.attribute-list.truncate', [
												UL(showMore ? '.thin-list' : null , [
													displayList?.map(
														(termChild) => {
															let childMatch = onlyChildMatch( displayList, parentLabel, termChild.name)
															return childMatch ? null : createAttribute(termChild)
														}
													)
												]),
												moreContent ? Button(`#more-${parentLabel}.more-button`, {
													content: 'More',
													onclick: () => {
														contentExpand(parentLabelTrimmed)
													},
												}) : null,
												])
											]),
											Div('.more-content', [
												createAttributeHeader(parentLabel),
												Div('.attribute-list.truncate', [
												// We don't need to filter this list as we know it will have more than one child
												UL('.thin-list', characteristics?.map((termChild) => createAttribute(termChild))),
												Button(`#more-${parentLabelTrimmed}.hide-button`, {
													content: 'Hide',
													onclick: () => {
														contentExpand(parentLabelTrimmed)
													},
												})
												])
											])
										],
									})
								}) : 'No condition specified'
							]),
						])
					}
					break
				case 'interventions':
					{
						// Rather than fullList interventions are labeled with interventionList
						let groups = (study.groups || []).concat(AnnotatedAbstract.getAnnotations((await annotModel?.annotatedAbstract)?.annotations || [], structuredQuery?.interventions, 'intervention'))
						let interventionList = _.groupBy(groups, 'analyzeAs')
						delete interventionList.undefined // don't want the undefined groups
						interventionList = Object.keys(interventionList).map(key => ({
							analyzeAs: key,
							groups: interventionList[key]
						}))
						// add more link to displayLength dependent on groupLength
						return Div(`#interventions.term-dropdown-container${(interventionList.length > 0) ? '': '.collapse' }`, [
							Div('.term-header', {
								content: Div('.display-flex', [
									Div('.display-flex', [
										H2('.term-title', [(interventionList.length > 1) ? 'Treatments' : 'Treatment']),
										Div('.matching-percent', [
											(interventionList.length > 0) ? asPercentage(study.interventionMatchingScore) : null,
											ArrowIcon('interventions', true)
										]),
									]),
								]),
								onclick: () => {
									containerCollapse('interventions')
								},
							}),
							Div('#intervention-body.term-body', [
								// intervention headers
								(interventionList.length > 0) ? interventionList.map(({ analyzeAs, groups }) => {
									parentLabel = analyzeAs
									let parentLabelTrimmed = parentLabel.replace(/\s/g, "");
									let showMore = groups.length > 2
									// Display groupList then makes sure to show the two groups under that intervention that we want to display

									let displayGroupList = showMore ? groups.slice(0, 2) : groups
									let moreContent = groups

									return Div(`#${parentLabelTrimmed}.parent-container`, {
										// used to switch div content within parent container
										attributes: { dir: 'hor' },
										content: [
											Div('.initial-content', [
												createAttributeHeader(parentLabel),
												Div('.attribute-list.truncate', [
													UL(showMore ? '.thin-list' : null , [
														displayGroupList?.map((group, index) => {
															let childMatch = onlyChildMatch(displayGroupList, parentLabel, group.OriginalName || group.text)
															return childMatch ? null : createAttribute({
																	name: group.OriginalName || group.text,
																	matchingScore: group.matchingScore,
																	as: group.analyzeAs,
																	parent: parentLabel,
															})
														})
													]),
													showMore ? Button(`#more-${parentLabelTrimmed}.more-button`, {
														content: 'More',
														onclick: () => {
															contentExpand(parentLabelTrimmed)
														},
													}) : null,
												]),
											]),
											Div('.more-content', [
												createAttributeHeader(parentLabel),
												Div('.attribute-list.truncate', [
													// We don't need to filter this list as we know it will have more than one child
													UL('.thin-list',
														moreContent.map((group, index) => {
															return createAttribute({
																name: group.OriginalName || group.text,
																matchingScore: group.matchingScore,
																as: group.analyzeAs,
																parent: parentLabelTrimmed,
															})
														})),
													Button(`#more-${parentLabelTrimmed}.hide-button`, {
														content: 'Hide',
														onclick: () => {
															contentExpand(parentLabelTrimmed)
														},
													})
												])
											])
										],
									})
								}) : 'No treatment specified'
							]),
						])
					}
					break
			}
		}
		this.append(
			study.matchingScore ? Label('.matching-score', [
				'Estimated likelihood of relevance: ' + Math.round(study.matchingScore * 100) + '%',
			]) : null,
			Div(reactive((async () => ([
				await picoContainer('design'),
				await picoContainer('characteristics'),
				await picoContainer('interventions'),
				await picoContainer('outcomes'),
				await picoContainer('publications'),
			]))())),
		)
	}
}

function asPercentage(ratio) {
	if (isNaN(ratio))
		return ''
	let percent = 100 * ratio
	return (percent > 4 ? Math.round(percent) : percent > 0.4 ? percent.toFixed(1) : percent.toFixed(2)) + '%'
}

class GroupEditingDialog extends Dialog.with('.group-editing-dialog') {
    get defaults() {
		return {
			head: {
				titleText: 'Edit Trial Groupings',
			},
			id: 'group-editing-dialog',
			size: '500px',
			foot: {},
		}
	}
    ready(properties) {

        const { removePublication, relocatePublication, termChild, publicationList } = properties
        this.body.append(
			publicationList.length > 1
				? [
					P(`Remove the selected study from its trial group by clicking the Remove Study button below`), Br,
					Span(['To assign the current study to a new group instead, click Reassign Study below'])
				]	
				: [
					Span(['To assign the current study to a new group, click Reassign Study below'])
				]
        )
		
		this.footer.rightControls.append(Button('.btn.secondary', {
			textContent: 'Reassign Study',
			onclick: () => {
				new GroupReassignmentDialog({ study: termChild, parent, relocatePublication }).show()
				return this.hide()
			}
		}))
		if (publicationList.length > 1) {
			this.footer.leftControls.append(Button('.btn.secondary', {
				textContent: 'Remove Study',
				width: '10em',
				onclick: () => {
					removePublication(termChild)
					return this.hide()
				}
			}))
		}
    }
    show() {
        super.show()
    }
}
